/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ToDoItem, ToDoOfferItemToDoStatusEnum } from 'client-code/recordings';

import { Log } from '../../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../../core/navigation';
import {
    getTodoItemsFromPage,
    LIST_ITEMS_MAX_COUNT,
    LIST_ROWS_ON_PAGE,
} from '../../../../views/SettingsScreen/Manage/ToDoList/ToDoList.utils';
import {
    onKeyBackNoNetworkOverlay,
    onKeyOkNoNetworkOverlay,
} from '../../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import { resetPCPinValuesAction } from '../../../overlay/pcpinoverlay/pcpinoverlay.actions';
import {
    hidePcPinOverlay,
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { hideOverlayDialog } from '../../../overlaydialog/overlaydialog.reducers';
import { showGuideScreen, showPreviousScreen } from '../../../screens/screens.reducers';
import { goToToDoListNextPageAction, goToToDoListPreviousPageAction } from '../todolist.actions';
import {
    handleToDoListErrorOrNetwork,
    onKeyOkNoTaskToDoList,
    resetToDoListState,
    showRecordingConflictInfoOverlay,
} from '../todolist.reducers';
import { onKeyDownToDoListAction, onKeyUpToDoListAction } from './todolist.navigation.actions';

const TAG = 'ToDoListNavigationReducers';

export const onKeyBackToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(
                resetToDoListState({
                    resetError: true,
                    resetCounter: true,
                }),
            );
            dispatch(showPreviousScreen());
            return;

        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyBackNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_CONFLICT_INFO_OVERLAY:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        default:
    }
};

export const onKeyOkToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { isError } = getState().todoList;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            if (handleToDoListErrorOrNetwork(dispatch, isError)) return;
            dispatch(onKeyOkNoTaskToDoList());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyOkNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_CONFLICT_INFO_OVERLAY:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        default:
    }
};

export const onKeyUpToDoList = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, currentPage, toDoItems, isError } = getState().todoList;
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.NO_TASK: {
            if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

            if (verticalPosition <= 0) {
                if (validateCanGoToPreviousPageToDoList(currentPage)) {
                    const { actualItemsLength } = getTodoItemsFromPage(toDoItems, currentPage - 1);
                    dispatch(goToToDoListPreviousPageAction(actualItemsLength - 1));
                }
            } else {
                dispatch(onKeyUpToDoListAction(verticalPosition - 1));
            }
            break;
        }
        default:
    }
};

export const onKeyDownToDoList = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, currentPage, toDoItems, totalCount, isError } = getState().todoList;
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.NO_TASK: {
            if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

            const { actualItemsLength } = getTodoItemsFromPage(toDoItems, currentPage);

            if (verticalPosition < actualItemsLength - 1) {
                dispatch(onKeyDownToDoListAction(verticalPosition + 1));
                return;
            }

            if (validateCanGoToNextPageToDoList(toDoItems, currentPage, totalCount)) {
                dispatch(goToToDoListNextPageAction());
            }
            break;
        }
        default:
    }
};

export const onKeyInfoToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { toDoItems, currentPage, verticalPosition, isError } = getState().todoList;

    if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
        return;
    }
    if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

    const { actualItems } = getTodoItemsFromPage(toDoItems, currentPage);
    const currentItem = actualItems?.[verticalPosition];

    if (!currentItem) {
        Log.w(TAG, 'No current item found. Info Action is ignored.');
        return;
    }

    if (currentItem.toDoStatus === ToDoOfferItemToDoStatusEnum.Conflict) {
        dispatch(showRecordingConflictInfoOverlay(currentItem));
    }
};

export const onKeyChannelUpToDoList = () => (dispatch: Function, getState: Function) => {
    const { currentPage, toDoItems, isError } = getState().todoList;
    const { screenTaskController } = getState().screens;

    if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
        return;
    }

    if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

    if (validateCanGoToPreviousPageToDoList(currentPage)) {
        const { actualItemsLength } = getTodoItemsFromPage(toDoItems, currentPage - 1);
        dispatch(goToToDoListPreviousPageAction(actualItemsLength - 1));
    }
};

export const onKeyChannelDownToDoList = () => (dispatch: Function, getState: Function) => {
    const { currentPage, toDoItems, totalCount, isError } = getState().todoList;
    const { screenTaskController } = getState().screens;

    if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
        return;
    }

    if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

    if (validateCanGoToNextPageToDoList(toDoItems, currentPage, totalCount)) {
        dispatch(goToToDoListNextPageAction());
    }
};

export const onKeyDigitToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        default:
    }
};

export const onKeyGuideToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(showGuideScreen());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(resetPCPinValuesAction());
            dispatch(hidePcPinOverlay());
            break;
        default:
    }
};

export const onKeyLeftToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        default:
    }
};

export const onKeyRightToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        default:
    }
};

export const onMouseWheelToDoList =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.NO_TASK:
                if (wheelDeltaY < 0) {
                    dispatch(onKeyChannelDownToDoList());
                } else if (wheelDeltaY > 0) {
                    dispatch(onKeyChannelUpToDoList());
                }
                break;
            default:
        }
    };

export const validateCanGoToPreviousPageToDoList = (currentPage: number) => {
    if (currentPage > 0) {
        return true;
    }
    Log.i(TAG, 'Already on the first page. Action is ignored.');
    return false;
};

export const validateCanGoToNextPageToDoList = (
    toDoItems: ToDoItem[],
    currentPage: number,
    totalCount: number,
) => {
    const finalPage = Math.ceil(totalCount / LIST_ROWS_ON_PAGE) - 1;
    const hardFinalPage = Math.ceil(LIST_ITEMS_MAX_COUNT / LIST_ROWS_ON_PAGE) - 1;
    if (currentPage === finalPage || currentPage === hardFinalPage) {
        Log.i(TAG, 'Already on the last page. Action is ignored.');
        return false;
    }
    const hasItemsOnThisPage = toDoItems.length > currentPage * LIST_ROWS_ON_PAGE;
    if (!hasItemsOnThisPage) {
        Log.i(TAG, 'User is currently waiting for items to load. Action is ignored.');
        return false;
    }
    return true;
};
